import { ApolloError } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import * as Sentry from '@sentry/react';
import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ConnectedProps, connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { notify } from '@/actions/ui';
import login, {
  PendingSocialLink,
  SignupType,
  defaultNext,
  formatAuthPath,
  isSameEmail,
  loginWithSocial,
} from '@/auth';
import { SocialConnectStep } from '@/auth/components/SocialConnectStep/SocialConnectStep';
import SocialSignInButtons, {
  isSocialSignInEnabled,
} from '@/auth/components/SocialSignInButtons/SocialSignInButtons';
import { SocialSignupChoice } from '@/auth/components/SocialSignupChoice/SocialSignupChoice';
import { setBasicAuth } from '@/auth/store';
import { codeSentMessage } from '@/auth/utils/codeDestination';
import { isCognitoLoginEnabled } from '@/auth/utils/cognitoSession';
import { SIGN_IN_EXPIRED_MESSAGE } from '@/auth/utils/cognitoSignIn';
import { handleCredentialsMissing } from '@/auth/utils/credentialsMissing';
import { readPendingSocialLink } from '@/auth/utils/pendingSocialLinkHandoff';
import { readSignInHandoff } from '@/auth/utils/signInHandoff';
import { SocialProvider, socialProviderName, startSocialSignIn } from '@/auth/utils/socialSignIn';
import { socialSignInFailure } from '@/auth/utils/socialSignInFailure';
import { forgetSocialSignup, socialSignupState } from '@/auth/utils/socialSignupHandoff';
import Alert from '@/components/Alert';
import Dialog from '@/components/Dialog/Dialog';
import Link from '@/components/Link';
import { APIError, ERROR_CODES, OtpMethod, UnauthorizedError, hasErrorCode } from '@/core/api';
import { GroupDomain } from '@/group';
import { useApp } from '@/hooks/useAppContext';
import { RootState } from '@/store';
import { white } from '@/theme/colors';

import CognitoMfaSetup from './CognitoMfaSetup';
import Credentials from './Credentials';
import EnrollOtpAuth from './EnrollOtpAuth';
import VerificationCode from './VerificationCode';

const useStyles = makeStyles({
  loginErrorLink: {
    '&:hover': {
      textDecoration: 'underline',
    },
    color: white,
    textDecoration: 'underline',
  },
  LoginErrorSubText: {
    fontSize: 14,
  },
});

function makeSentence(s: string) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const SECURITY_EMAIL = 'hello@onfrontiers.com';

type Classes = ReturnType<typeof useStyles>;

function invalidCredentialsMessage(err: UnauthorizedError, s: Classes): ReactNode {
  const message = 'Invalid username or password';
  // Cognito gives no remaining-attempts count (decision Q6), and a migrated
  // user without a password lands here too: always offer the reset.
  if (isCognitoLoginEnabled()) {
    return (
      <span>
        {message}.&nbsp;
        <Link className={s.loginErrorLink} to="/password_reset">
          Reset your password
        </Link>
      </span>
    );
  }
  const remaining = err.remainingAttempts ?? 0;
  if (remaining <= 0 || remaining >= 3) return message;
  return (
    <span>
      {message}. To prevent your account from being locked,&nbsp;
      <Link className={s.loginErrorLink} to="/password_reset">
        recover your password
      </Link>
    </span>
  );
}

function lockedMessage(s: Classes): ReactNode {
  return (
    <span>
      Your account has been locked for 24 hours
      <br />
      <div className={s.LoginErrorSubText}>
        If you need urgent assistance please email&nbsp;
        <a className={s.loginErrorLink} href={`mailto:${SECURITY_EMAIL}`}>
          {SECURITY_EMAIL}
        </a>
      </div>
    </span>
  );
}

interface LoginProps {
  initialErrors?: string[];
  nextUrl?: string;
  signupType?: SignupType;
  invite?: string;
  domain?: GroupDomain;
}

const connector = connect(
  (state: RootState) => ({
    initialErrors: state.runtime.errors,
  }),
  {
    notify,
    setBasicAuth,
  }
);

type Step = 'initial' | 'mfa' | 'mfa_setup';

interface OTPConfiguration {
  method?: OtpMethod;
  maskedAddress?: string;
}

// ENG-6165: what "Continue with Google" (or, ENG-6169, Microsoft) found when it
// could not sign in. The ID token of a CONFIRM_ACCOUNT answer waits, in this
// component only, for a password sign-in to the account it named. An answer
// that named no account (ambiguous) carries no link: there is nothing to check
// the sign-in against. ENG-6192: CONNECT is an answer the api lets the page
// confirm without the password (proof NONE or EMAIL_CODE).
type SocialOutcome = { provider: SocialProvider } & (
  | {
      status: 'CONNECT';
      email: string;
      proof: 'NONE' | 'EMAIL_CODE';
      token: string;
      idToken: string;
    }
  | { status: 'CONFIRM_ACCOUNT'; email: string; pendingLink: PendingSocialLink }
  | { status: 'CONFIRM_ACCOUNT'; email?: undefined; pendingLink?: undefined }
  | { status: 'SIGN_UP'; email: string; signupState: ReturnType<typeof socialSignupState> }
);

// mt-24: the same 24px gap below the buttons as around their divider.
function SocialNotice({ outcome }: { outcome: SocialOutcome }) {
  if (outcome.status !== 'CONFIRM_ACCOUNT') return null;
  const name = socialProviderName(outcome.provider);
  return (
    <Alert preset="notification" center role="status" className="mt-24">
      {outcome.email
        ? `An OnFrontiers account already uses ${outcome.email}. Sign in with your password to connect ${name}.`
        : `An OnFrontiers account already uses your ${name} email. Log in with your email and password.`}
    </Alert>
  );
}

const Login = ({
  initialErrors = [],
  notify,
  setBasicAuth,
  nextUrl,
  signupType,
  invite,
  domain,
}: LoginProps & ConnectedProps<typeof connector>) => {
  const s = useStyles();
  const { graphql } = useApp();
  const [step, setStep] = useState<Step>('initial');
  const location = useLocation();
  const navigate = useNavigate();
  // ENG-6309: a signup form's provider answer that named this account.
  const [socialOutcome, setSocialOutcome] = useState<SocialOutcome | undefined>(() => {
    const link = readPendingSocialLink(location.state);
    return (
      link && {
        provider: link.provider,
        status: 'CONFIRM_ACCOUNT',
        email: link.email,
        pendingLink: link,
      }
    );
  });
  const pendingSocialLink =
    socialOutcome?.status === 'CONFIRM_ACCOUNT' ? socialOutcome.pendingLink : undefined;
  const [groupId, setGroupId] = useState<string | undefined>(undefined);
  // ENG-6103: true when this sign-in runs through Cognito, which enrols TOTP
  // itself and never needs the password again.
  const [cognitoEnrolment, setCognitoEnrolment] = useState(false);
  const [otpConfiguration, setOtpConfiguration] = useState<OTPConfiguration>({});
  // A signup that created the account but couldn't sign the user in sends
  // them here with the email they used (ENG-5760).
  const handoff = readSignInHandoff(location.state);
  const [email, setEmail] = useState(handoff.email ?? '');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState([]);
  const [finalAttemptDialogOpen, setFinalAttemptDialogOpen] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [buttonResendDisable, setButtonResendDisable] = useState(false);

  // AlertId used in the automation tests
  const [alertId, setAlertId] = useState<string | undefined>(undefined);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleEmail = useCallback((e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const typed = e.target.value;
    setEmail(typed);
    // ENG-6165: a different address drops the provider link offered for the
    // named one, and the notice that promised it.
    setSocialOutcome((outcome) =>
      outcome?.status === 'CONFIRM_ACCOUNT' &&
      outcome.pendingLink &&
      !isSameEmail(typed, outcome.pendingLink.email)
        ? undefined
        : outcome
    );
  }, []);
  const handlePassword = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setPassword(e.target.value),
    []
  );
  const handleOtp = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setOtp(e.target.value),
    []
  );
  const hasTypedOtp = useMemo(() => otp.length > 0, [otp]);

  const handleCancel = useCallback(() => {
    setOtp('');
    setErrors([]);
    setAlertId(undefined);
    setStep('initial');
    setOtpConfiguration({});
    setButtonDisable(false);
    setButtonResendDisable(false);
  }, []);

  // ENG-6103: one call at a time. A second code would answer a challenge
  // Cognito already consumed, and Resend starts a fresh sign-in that would
  // replace the challenge a pending submit answers.
  const submitting = useRef(false);
  const [submitPending, setSubmitPending] = useState(false);

  // Resolves true once a new code was sent; the code screen's countdown paces the next one.
  const handleResend = async (): Promise<boolean> => {
    if (submitting.current) return false;
    submitting.current = true;
    setButtonResendDisable(true);
    try {
      const recaptchaToken = executeRecaptcha ? await executeRecaptcha('login') : '';
      await login({ email, password, next: nextUrl, recaptchaToken, pendingSocialLink });
    } catch (err: unknown) {
      if (err instanceof UnauthorizedError && hasErrorCode(err, ERROR_CODES.AUTH_MFA_REQUIRED)) {
        const sent = err.otpMethod && codeSentMessage(err.otpMethod, err.otpMaskedAddress ?? '');
        notify(sent || 'We sent a new code.');
        return true;
      }
      return false;
    } finally {
      submitting.current = false;
      setButtonResendDisable(false);
    }
    console.warn('Unexpected login success without otp');
    return false;
  };

  // The Cognito sign-in session lapsed: back to the password form.
  const restartSignIn = useCallback(() => {
    handleCancel();
    setCognitoEnrolment(false);
    setAlertId('invalidLoginAlert');
    // @ts-expect-error TS(2345): errors is typed never[] (see setErrors below)
    setErrors([SIGN_IN_EXPIRED_MESSAGE]);
  }, [handleCancel]);

  const handleSubmit = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setSubmitPending(true);
    setButtonDisable(true);

    try {
      const recaptchaToken = executeRecaptcha ? await executeRecaptcha('login') : '';
      await login({ email, password, otp, next: nextUrl, recaptchaToken, pendingSocialLink });
    } catch (err) {
      let message;
      const credentialsMissing = handleCredentialsMissing(err);

      if (credentialsMissing) {
        message = credentialsMissing;
      } else if (err instanceof ApolloError) {
        Sentry.captureException(err);
        message = 'Sorry, the service is currently unavailable.';
      } else if (err instanceof UnauthorizedError) {
        switch (err.code) {
          case ERROR_CODES.AUTH_INVALID_CREDENTIALS:
            message = invalidCredentialsMessage(err, s);
            if (!isCognitoLoginEnabled() && err.remainingAttempts === 1) {
              setFinalAttemptDialogOpen(true);
            }
            break;
          case ERROR_CODES.AUTH_LOCKED_FOR_INTERVAL:
            message = lockedMessage(s);
            break;
          case ERROR_CODES.AUTH_MFA_SETUP_REQUIRED:
            message = 'Two-factor authentication is required';
            if (step !== 'mfa_setup' && isCognitoLoginEnabled()) {
              setCognitoEnrolment(true);
              setStep('mfa_setup');
            } else if (step !== 'mfa_setup') {
              await setBasicAuth(email, password);
              setGroupId(err.mfaGroupId);
              setStep('mfa_setup');
            }
            break;
          case ERROR_CODES.AUTH_MFA_REQUIRED:
            if (step !== 'mfa') {
              setOtpConfiguration({
                method: err.otpMethod,
                maskedAddress: err.otpMaskedAddress,
              });
              setStep('mfa');
            }
            // Not a failure: the code form is usable at once.
            setButtonDisable(false);
            break;
          case ERROR_CODES.AUTH_OTP_INVALID:
            message = 'Invalid verification code';
            break;
          case ERROR_CODES.AUTH_TOKEN_EXPIRED:
            // ENG-6103: the Cognito challenge session lapsed; start over.
            message = err.message;
            handleCancel();
            setCognitoEnrolment(false);
            break;
          default:
            message = makeSentence(err.message);
        }
      } else if (err instanceof APIError) {
        Sentry.captureException(err);
        message = 'Sorry, unknown error.';
      } else {
        Sentry.captureException(err);
        message = 'Sorry, an error occurred. Please try again.';
      }

      setAlertId('invalidLoginAlert');
      setTimeout(() => {
        setButtonDisable(false);
      }, 1000);
      // @ts-expect-error TS(2345): Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
      setErrors(message ? [message] : []);
      setOtp('');
    } finally {
      submitting.current = false;
      setSubmitPending(false);
    }
  };

  const showSocialFailure = (provider: SocialProvider, err: unknown) => {
    const { message, expected } = socialSignInFailure(err, provider);
    if (!expected) Sentry.captureException(err);
    setAlertId('invalidLoginAlert');
    // @ts-expect-error TS(2345): errors is typed never[] (see setErrors above)
    setErrors([message]);
  };

  const handleSocialCredential = async (provider: SocialProvider, idToken: string) => {
    if (submitting.current) return;
    submitting.current = true;
    setButtonDisable(true);
    let redirecting = false;
    try {
      const start = await startSocialSignIn(graphql, provider, idToken);
      if (start.status === 'SIGN_IN') {
        await loginWithSocial(graphql, {
          provider,
          username: start.username,
          idToken,
          next: nextUrl,
        });
        redirecting = true;
        return;
      }
      setErrors([]);
      setAlertId(undefined);
      if (start.status === 'CONFIRM_ACCOUNT') {
        if (start.email && start.connect?.token) {
          setSocialOutcome({
            provider,
            status: 'CONNECT',
            email: start.email,
            proof: start.connect.proof,
            token: start.connect.token,
            idToken,
          });
        } else if (start.email) {
          setEmail(start.email);
          setSocialOutcome({
            provider,
            status: 'CONFIRM_ACCOUNT',
            email: start.email,
            pendingLink: { provider, idToken, email: start.email },
          });
        } else {
          setSocialOutcome({ provider, status: 'CONFIRM_ACCOUNT' });
        }
      } else {
        const signupState = socialSignupState({
          social: { provider, signupToken: start.signupToken, idToken },
          email: start.email,
          firstName: start.firstName,
          lastName: start.lastName,
        });
        // A login that names its signup type (an invite's /login/member, a
        // network's /acme/login/expert) already knows the form.
        if (signupType) {
          navigate(formatAuthPath({ to: '/signup', domain, signupType, invite, next: nextUrl }), {
            state: signupState,
          });
          return;
        }
        setSocialOutcome({ provider, status: 'SIGN_UP', email: start.email, signupState });
      }
    } catch (err) {
      showSocialFailure(provider, err);
    } finally {
      submitting.current = false;
      if (!redirecting) setButtonDisable(false);
    }
  };

  // "Use a different account" lands on the email field it brings back.
  const emailInput = useRef<HTMLInputElement>(null);
  const refocusEmail = useRef(false);
  useEffect(() => {
    if (!refocusEmail.current || !emailInput.current) return;
    refocusEmail.current = false;
    emailInput.current.focus();
  }, [socialOutcome]);

  // ENG-6311: the role choice's two forms, which take the sign-up from router state.
  const signupPath = (to: string) => formatAuthPath({ to, domain, invite, next: nextUrl });

  const socialSection = isSocialSignInEnabled() ? (
    <>
      <SocialSignInButtons
        text="continue_with"
        dividerPlacement="above"
        dividerLabel="or continue with"
        onCredential={handleSocialCredential}
        onError={showSocialFailure}
        disabled={buttonDisable}
      />
      {socialOutcome && <SocialNotice outcome={socialOutcome} />}
    </>
  ) : undefined;

  const allErrors = [...errors, ...initialErrors];

  switch (step) {
    case 'initial':
      if (socialOutcome?.status === 'CONNECT') {
        const { provider, email: connectEmail, proof, token, idToken } = socialOutcome;
        return (
          <SocialConnectStep
            provider={provider}
            email={connectEmail}
            proof={proof}
            token={token}
            onConnected={(username) =>
              loginWithSocial(graphql, {
                provider,
                username,
                idToken,
                next: nextUrl,
                connected: true,
              })
            }
            onCancel={() => setSocialOutcome(undefined)}
          />
        );
      }
      if (socialOutcome?.status === 'SIGN_UP') {
        const { email: signupEmail, signupState } = socialOutcome;
        return (
          <SocialSignupChoice
            email={signupEmail}
            customerPath={signupPath('/signup/customer')}
            expertPath={signupPath('/signup/expert')}
            signupState={signupState}
            onUseDifferentAccount={() => {
              forgetSocialSignup(signupState);
              refocusEmail.current = true;
              setSocialOutcome(undefined);
            }}
          />
        );
      }
      return (
        <>
          <Credentials
            invite={invite}
            domain={domain}
            signupType={signupType}
            nextUrl={nextUrl}
            email={email}
            password={password}
            onEmailChange={handleEmail}
            onPasswordChange={handlePassword}
            onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              handleSubmit();
            }}
            errors={allErrors}
            notice={handoff.notice}
            buttonDisable={buttonDisable}
            alertId={alertId}
            afterSubmit={socialSection}
            emailRef={emailInput}
          />
          <Dialog
            PaperProps={{
              id: 'invalidUserPasswordPopUp',
            }}
            open={finalAttemptDialogOpen}
            onCancel={() => {
              setFinalAttemptDialogOpen(false);
            }}
          >
            <span>
              You only have one remaining opportunity to input your password before you are locked
              out.&nbsp;
              <Link to="/password_reset">Recover your password</Link>.
            </span>
          </Dialog>
        </>
      );
    case 'mfa_setup': {
      if (cognitoEnrolment) {
        return (
          <CognitoMfaSetup
            email={email}
            next={nextUrl || defaultNext}
            onExpired={restartSignIn}
            pendingSocialLink={pendingSocialLink}
          />
        );
      }
      if (!groupId) {
        throw new Error('MFA setup requires a group ID');
      }
      return <EnrollOtpAuth groupId={groupId} setOtp={setOtp} email={email} />;
    }
    case 'mfa':
      // A Cognito authenticator-app challenge has no address to show ('').
      if (
        !otpConfiguration.method ||
        (isCognitoLoginEnabled()
          ? otpConfiguration.maskedAddress === undefined
          : !otpConfiguration.maskedAddress)
      ) {
        throw new Error('MFA step requires a method and masked address');
      }
      return (
        <VerificationCode
          code={otp}
          onChange={handleOtp}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onResend={handleResend}
          disabled={!hasTypedOtp || buttonDisable}
          buttonResendDisable={buttonResendDisable || submitPending}
          error={errors && errors[0]}
          method={otpConfiguration.method}
          maskedAddress={otpConfiguration.maskedAddress ?? ''}
        />
      );
  }
};

export default connector(Login);
